"use client";

import type { LeadFormExitMethod } from "@homeinstead/analytics";

import {
  buildLeadFormExitEvent,
  buildLeadFormStepEvent,
  track,
} from "@homeinstead/analytics";
import { LEAD_FORM_DELIVERY_CONTEXT } from "@homeinstead/forms/lead-form";
import { EventName } from "@homeinstead/shared";
import { useMemo } from "react";
import { useLeadFormAnalytics } from "./LeadFormAnalyticsContext";

/**
 * Returns stable `leadFormStep`/`leadFormExit` callbacks for firing the LFB
 * Segment events, or null if rendered outside a LeadFormAnalyticsProvider
 * (e.g. Storybook, tests).
 *
 * Usage: `trackLeadForm?.leadFormStep({ stepName, userSelection })`.
 * Usage: `trackLeadForm?.leadFormExit({ stepName, exitMethod })`.
 *
 * stepName: resolved as step.metricsLabel ?? step.id at the call site.
 * userSelection: field-keyed object of the values entered/selected on this
 *   step. Multi-select fields are arrays; single-value fields are strings.
 *   Omitted for plain Continue clicks (defaults to {} in the payload).
 *
 * The returned object is memoized on the context value. It is safe to use in
 * useCallback dependency arrays.
 *
 * Page location is added later by analytics source middleware.
 */
function useTrackLeadFormEvent() {
  const config = useLeadFormAnalytics();

  return useMemo(() => {
    if (!config) {
      // Null is the designed graceful degradation for contexts without a
      // provider (Storybook, unit tests). Consumers use: trackLeadForm?.leadFormStep().
      return null;
    }

    const isNationalLeadForm = !config.deliveryContext
      ? config.franchiseId === 0
      : config.deliveryContext === LEAD_FORM_DELIVERY_CONTEXT.national;

    return {
      leadFormExit: ({
        stepName,
        exitMethod,
      }: {
        stepName: string;
        exitMethod: LeadFormExitMethod;
      }) =>
        track(
          EventName.LEAD_FORM_EXIT,
          buildLeadFormExitEvent({
            exitMethod,
            formVariant: "lfb",
            formVersionId: config.versionId,
            isCorporate: isNationalLeadForm,
            pageZone: config.pageZone,
            stepName,
          }),
        ),
      leadFormStep: ({
        stepName,
        userSelection,
      }: {
        stepName: string;
        userSelection?: Record<string, string | string[]>;
      }) =>
        track(
          EventName.LEAD_FORM_STEP,
          buildLeadFormStepEvent({
            formVariant: "lfb",
            formVersionId: config.versionId,
            isCorporate: isNationalLeadForm,
            pageZone: config.pageZone,
            stepName,
            userSelection,
          }),
        ),
    };
  }, [config]);
}

export { useTrackLeadFormEvent };
